

































































import { Vue, Prop, Component, Watch } from 'vue-property-decorator';
import {
    CreateFavoriteListRequest,
    FavoriteListPermission,
    FavoriteListViewObject,
    FavoritesItemsListedViewObject,
    ProductDetailsViewModel,
    ProductTileViewObject
} from '@/types/apiServerContract';
import ProductFavoriteItemsService from '@/project/product/services/productFavoriteItems.service';
import SpinnerOverlay from '@/project/spinners/SpinnerOverlay.vue';
import myAccountStore from '@/project/app/client-pages/my-account/my-account.store';
import loggingService from '@/core/logging.service';
import bus from '@/core/bus';
import dictionaryService from '@/core/translation/dictionary.service';
import { REMOVE_PRODUCT_TILE } from '@/project/app/client-pages/my-account/constants';
import FavoriteListCrudOverlayService from '@/project/modals/overlays/FavoriteListCrudOverlay.service';
import loginFormOverlayService from '@/project/modals/overlays/LoginFormOverlay.service';

interface FavoriteListViewObjectExtended extends FavoriteListViewObject {
    checked: boolean;
    loading: boolean;
}

@Component({
    components: { SpinnerOverlay }
})
export default class ProductTileFavorite extends Vue {
    @Prop({ required: true })
    product!: ProductTileViewObject | ProductDetailsViewModel;

    @Prop({ required: true })
    isLoggedIn!: boolean;

    loadingLists: boolean = false;
    showOverlay: boolean = false;
    showCreateFavouriteListOverlay: boolean = false;
    listInformation: FavoritesItemsListedViewObject = {
        itemId: this.product.itemId,
        listIds: []
    };

    get favoriteLists(): FavoriteListViewObject[] {
        return myAccountStore.allFavouriteLists.map(x => ({
            ...x,
            checked: this.isItemOnList(x)
        }) as FavoriteListViewObjectExtended)
            .filter(x => this.canEditList(x) || this.isItemOnList(x));
    }

    isItemOnList(list: FavoriteListViewObject) {
        return this.listInformation.listIds.some(x => x === list.id);
    }

    canEditList(list: FavoriteListViewObject) {
        return list.permission === FavoriteListPermission.Edit;
    }

    titleForList(list: FavoriteListViewObject) {
        if (this.canEditList(list)) return null;

        return dictionaryService.get('FavoriteList.Products.NoPermission');
    }

    get isOnList() {
        return this.listInformation.listIds.length > 0;
    }

    get icon() {
        return this.isOnList ? 'favorite' : 'favorite-outline';
    }

    async createList() {
        if (!this.isLoggedIn) return;

        this.showCreateFavouriteListOverlay = true;

        try {
            await FavoriteListCrudOverlayService.show(null, false, 'create');
        } finally {
            this.showCreateFavouriteListOverlay = false;
        }
    }

    openOverlay() {
        this.showOverlay = true;
    }

    closeOverlay() {
        // Ensure that we don't triggert the outside click whent he create modal is open
        if (this.showCreateFavouriteListOverlay) { return; }

        bus.emit(REMOVE_PRODUCT_TILE, this.listInformation);

        this.showOverlay = false;
    }

    get hasSlot() {
        if (this.$slots?.default !== undefined) {
            return true;
        }

        return false;
    }

    async updateList(list: FavoriteListViewObjectExtended) {
        if (list.loading || !this.isLoggedIn) {
            return;
        }

        list.loading = true;

        try {
            // The user doesn't have any favourite lists yet,
            // so we need to create the default one first
            if (list.id === myAccountStore.defaultList.id) {
                const createModel: CreateFavoriteListRequest = {
                    name: list.name,
                    description: list.description,
                    shareWithOrganization: [],
                    shareWithUsers: []
                };

                const updatedFavouriteLists = await myAccountStore.createFavouriteList(createModel);

                // This is a bit optimistic, but, ideally, the user should always have 1 list
                // after the creation request - our default list
                if (updatedFavouriteLists.private.length) {
                    list.id = updatedFavouriteLists.private[0].id;
                }
            }

            const index = this.listInformation.listIds.indexOf(list.id);

            if (list.checked && index < 0) {
                this.listInformation.listIds.push(list.id);
            } else if (index >= 0) {
                this.listInformation.listIds.splice(index, 1);
            }

            if (list.checked) {
                await myAccountStore.addFavoriteItem({
                    listId: list.id,
                    items: [
                        {
                            itemId: this.product.itemId,
                            quantity: 1
                        }
                    ]
                });
            } else {
                await myAccountStore.removeFavoriteItems({
                    listId: list.id,
                    itemIds: [this.product.itemId]
                });
            }
        } catch (e) {
            loggingService.error(e);
        } finally {
            list.loading = false;
        }
    }

    async checkIfProductIsListed() {
        if (!this.isLoggedIn || this.loadingLists) return;

        this.loadingLists = true;

        try {
            const request = ProductFavoriteItemsService.requestData(this.product.itemId);
            const result = await request.promise;
            if (result) {
                this.listInformation = result;
            }
        } catch {
            // No need to log any potential errors here since it's dealt with in our "AbstractBulkFetch" method.
        } finally {
            this.loadingLists = false;
        }
    }

    showLoginOverlay() {
        loginFormOverlayService.show('login');
    }

    @Watch('isLoggedIn')
    onLoginStateChange(state: boolean) {
        if (state) {
            this.checkIfProductIsListed();
        }
    }

    created() {
        this.checkIfProductIsListed();
    }
}
